import { TableComponent as DataTable } from 'components/TableComponent'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { Spinner } from 'reactstrap'
import styled from 'styled-components'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import MobilePeriodCard from './MobilePeriodCard'
import PeriodBreakdownRow from './PeriodBreakdownRow'

const ScrollableContainer = styled.div`
  position: relative;
  overflow-x: scroll;
  overflow-y: hidden;
  width: 100%;
  padding: 20px 0;
  margin-bottom: 10px;
  -webkit-overflow-scrolling: touch;

  /* Ensure scrollbar is always visible */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: ${(props) => (props.isMobile ? '6px' : '8px')};
    display: block !important;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: ${(props) => (props.isMobile ? '1px' : '5px')};
    display: block !important;
  }

  &::-webkit-scrollbar-thumb {
    background: #d4d4d4;
    border-radius: ${(props) => (props.isMobile ? '1px' : '5px')};
    display: block !important;
    min-width: 60px;
    &:hover {
      background: #c4c4c4;
    }
  }

  /* Firefox */
  scrollbar-width: thin !important;
  scrollbar-color: #d4d4d4 #f1f1f1;

  /* IE/Edge */
  -ms-overflow-style: scrollbar !important;

  /* Force scrollbar visibility */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: transparent;
    pointer-events: none;
  }

  /* Force minimum scroll area */
  &::after {
    content: '';
    display: block;
    min-width: ${(props) => (props.isMobile ? '100%' : '1200px')};
    height: 1px;
    opacity: 0;
  }
`

const ChartContainer = styled.div`
  min-width: ${(props) => (props.isMobile ? '560px' : '1200px')};

  .apexcharts-bar-series.apexcharts-plot-series .apexcharts-series path {
    clip-path: inset(0% 0% -5% 0% round ${(props) => (props.isMobile ? '1px' : '5px')});
  }
`

const MonthlyTab = ({ selectedMonthYear, dataCashFlow, res }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [selectedPoint, setSelectedPoint] = useState(null)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const generateMonthlyData = (selectedMonthYear, rawData) => {
    const selectedMonth = moment(selectedMonthYear).format('YYYY-MM')
    const daysInMonth = moment(selectedMonthYear).daysInMonth()

    const monthlyData = Array?.from({ length: daysInMonth }, (_, index) => {
      const fullDate = moment(
        `${selectedMonth}-${index + 1}`,
        'YYYY-MM-DD',
      ).format('D MMMM YYYY')
      return {
        date: fullDate,
        income: 0,
        expense: 0,
        profit: 0,
      }
    })

    rawData?.forEach((item) => {
      const dayIndex = moment(item?.createddate).date() - 1
      if (monthlyData[dayIndex]) {
        if (item?.incomeorexpense === 'income') {
          monthlyData[dayIndex].income += item?.amount
        } else if (item?.incomeorexpense === 'expense') {
          monthlyData[dayIndex].expense += item?.amount
        }

        monthlyData[dayIndex].profit =
          monthlyData[dayIndex].income - monthlyData[dayIndex].expense
      }
    })

    return monthlyData
  }

  const processedMonthlyData = generateMonthlyData(
    selectedMonthYear,
    dataCashFlow?.items || [],
  )

  const optionsMonthly = {
    chart: {
      type: 'bar',
      height: isMobile ? 320 : 500,
      zoom: { enabled: false },
      toolbar: { show: false },
      stacked: true,
      parentHeightOffset: 0,
      events: {
        dataPointSelection: (event, chartContext, config) => {
          if (!isMobile) return
          setSelectedPoint(config.dataPointIndex)
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        horizontal: false,
        columnWidth: isMobile ? '35%' : '50%',
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        distributed: false,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        shape: 'circle',
        size: isMobile ? 4 : 6,
      },
      fontSize: isMobile ? '10px' : '14px',
    },
    colors: ['#039855', '#D92D20'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    tooltip: {
      enabled: !isMobile,
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const period = w.globals.labels[dataPointIndex]

        let totalIncome = 0
        let totalExpense = 0
        const incomeItems = []
        const expenseItems = []

        w.config.series.forEach((s, idx) => {
          const val = series[idx][dataPointIndex]
          if (val > 0) {
            if (s.group === 'Pemasukan') {
              totalIncome += val
              incomeItems.push({ name: s.name, val, color: w.globals.colors[idx] })
            } else if (s.group === 'Pengeluaran') {
              totalExpense += val
              expenseItems.push({ name: s.name, val, color: w.globals.colors[idx] })
            }
          }
        })

        const profit = totalIncome - totalExpense

        if (totalIncome === 0 && totalExpense === 0) return ''

        let tooltipHtml = `<div class="p-2" style="font-size: 12px; background: white; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); min-width: 250px;">`
        tooltipHtml += `<div style="margin-bottom: 8px; text-align: center;"><strong>${period}</strong></div>`

        tooltipHtml += `<table style="width: 100%; border: 0; border-collapse: collapse;">`

        if (totalIncome > 0) {
          tooltipHtml += `
            <tr>
              <td colspan="3" style="padding-top: 4px; padding-bottom: 4px;"><strong>Total Pemasukan : Rp. ${newFormatThousandSeparator(totalIncome)}</strong></td>
            </tr>`
          incomeItems.forEach((item) => {
            const percentage = Math.round((item.val / totalIncome) * 100)
            tooltipHtml += `
            <tr>
              <td style="padding-left: 12px; padding-top: 2px; padding-bottom: 2px;">
                <div style="display:flex; align-items:center; gap:8px;">
                  <span style="width:10px; height:10px; background-color:${item.color}; display:inline-block; border-radius:50%"></span>
                  <span>${item.name}</span>
                </div>
              </td>
              <td style="padding: 2px 8px; width: 1%;">:</td>
              <td style="padding-top: 2px; padding-bottom: 2px; text-align: left; white-space: nowrap;">Rp. ${newFormatThousandSeparator(item.val)} (${percentage}%)</td>
            </tr>`
          })
        }

        if (totalExpense > 0) {
          tooltipHtml += `
            <tr>
              <td colspan="3" style="padding-top: ${totalIncome > 0 ? '12px' : '4px'}; padding-bottom: 4px;"><strong>Total Pengeluaran : Rp. ${newFormatThousandSeparator(totalExpense)}</strong></td>
            </tr>`
          expenseItems.forEach((item) => {
            const percentage = Math.round((item.val / totalExpense) * 100)
            tooltipHtml += `
            <tr>
              <td style="padding-left: 12px; padding-top: 2px; padding-bottom: 2px;">
                <div style="display:flex; align-items:center; gap:8px;">
                  <span style="width:10px; height:10px; background-color:${item.color}; display:inline-block; border-radius:50%"></span>
                  <span>${item.name}</span>
                </div>
              </td>
              <td style="padding: 2px 8px; width: 1%;">:</td>
              <td style="padding-top: 2px; padding-bottom: 2px; text-align: left; white-space: nowrap;">Rp. ${newFormatThousandSeparator(item.val)} (${percentage}%)</td>
            </tr>`
          })
        }

        tooltipHtml += `</table>`
        tooltipHtml += `<hr style="margin: 8px 0;" />`
        tooltipHtml += `<div><strong>Keuntungan ${period} : Rp. ${newFormatThousandSeparator(profit)}</strong></div>`
        tooltipHtml += `</div>`

        return tooltipHtml
      },
    },
    xaxis: {
      categories: processedMonthlyData.map((data) => data.date),
      labels: {
        style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
        rotate: -45,
        offsetX: 0,
        offsetY: 0,
        trim: isMobile,
        hideOverlappingLabels: isMobile,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tickPlacement: 'on',
      tickAmount: isMobile ? 15 : 31, // Show all days in the month
    },
    grid: {
      borderColor: '#f1f1f1',
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: isMobile ? 5 : 10,
      },
    },
    title: {
      text: 'Arus Kas Bulanan (Rupiah)',
      offsetY: isMobile ? 0 : 5,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          return newFormatThousandSeparator(value)
        },
        style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
        offsetX: isMobile ? -5 : 0,
        rotate: 0,
        align: 'right',
      },
      tickAmount: isMobile ? 4 : 5,
      min: 0,
      forceNiceScale: true,
      floating: false,
      decimalsInFloat: 0,
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
  }

  const daysInMonth = moment(selectedMonthYear).daysInMonth()
  const categoryData = {
    income: {},
    expense: {},
  }

  dataCashFlow?.items?.forEach((item) => {
    const dayIndex = moment(item.createddate).date() - 1
    const type = item.incomeorexpense
    const category = item.label || 'Lainnya'

    if (type === 'income') {
      if (!categoryData.income[category]) categoryData.income[category] = Array(daysInMonth).fill(0)
      categoryData.income[category][dayIndex] += item.amount
    } else if (type === 'expense') {
      if (!categoryData.expense[category]) categoryData.expense[category] = Array(daysInMonth).fill(0)
      categoryData.expense[category][dayIndex] += item.amount
    }
  })

  const seriesMonthly = []
  const chartColors = []

  const incomeColorPalette = ['#039855', '#12B76A', '#32D583', '#6CE9A6', '#A6F4C5', '#D1FADF']
  let colorIdx = 0
  Object.keys(categoryData.income).forEach((cat) => {
    seriesMonthly.push({
      name: cat,
      group: 'Pemasukan',
      data: categoryData.income[cat],
    })
    chartColors.push(incomeColorPalette[colorIdx % incomeColorPalette.length])
    colorIdx++
  })

  const expenseColorPalette = ['#D92D20', '#F04438', '#F97066', '#FDA29B', '#FECDCA', '#FEE4E2']
  colorIdx = 0
  Object.keys(categoryData.expense).forEach((cat) => {
    seriesMonthly.push({
      name: cat,
      group: 'Pengeluaran',
      data: categoryData.expense[cat],
    })
    chartColors.push(expenseColorPalette[colorIdx % expenseColorPalette.length])
    colorIdx++
  })

  if (seriesMonthly.length === 0) {
    seriesMonthly.push({
      name: 'Tidak Ada Data',
      group: 'Kosong',
      data: Array(daysInMonth).fill(0),
    })
    chartColors.push('#E0E0E0')
  }

  optionsMonthly.colors = chartColors

  const getPeriodBreakdown = (index) => {
    const items =
      dataCashFlow?.items?.filter(
        (item) => moment(item.createddate).date() - 1 === index,
      ) || []

    const income = {}
    const expense = {}

    items.forEach((item) => {
      const category = item.label || 'Lainnya'
      const bucket =
        item.incomeorexpense === 'income'
          ? income
          : item.incomeorexpense === 'expense'
            ? expense
            : null
      if (bucket) bucket[category] = (bucket[category] || 0) + item.amount
    })

    const toList = (map) =>
      Object.entries(map)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)

    return {
      period: processedMonthlyData[index]?.date,
      income: toList(income),
      expense: toList(expense),
    }
  }

  const periodRows = processedMonthlyData.map((row, index) => ({
    ...row,
    breakdown: getPeriodBreakdown(index),
  }))

  const columnsMonthly = [
    {
      name: <span>Periode</span>,
      selector: (row) => row?.date,
      wrap: true,
      grow: 2,
    },
    {
      name: <span>Pemasukan</span>,
      selector: (row) => `Rp${newFormatThousandSeparator(row.income)}`,
      wrap: true,
      right: true,
      grow: 2,
    },
    {
      name: <span>Pengeluaran</span>,
      selector: (row) => `Rp${newFormatThousandSeparator(row.expense)}`,
      wrap: true,
      right: true,
      grow: 2,
    },
    {
      name: <span>Untung/Rugi</span>,
      selector: (row) => (
        <span style={{ color: row.profit >= 0 ? '#027A48' : '#B42318' }}>
          {row.profit >= 0 ? '+ ' : '- '}
          Rp{newFormatThousandSeparator(Math.abs(row.profit))}
        </span>
      ),
      wrap: true,
      right: true,
      grow: 2,
    },
  ]

  return (
    <>
      <ScrollableContainer isMobile={isMobile}>
        <ChartContainer isMobile={isMobile}>
          <Chart
            options={optionsMonthly}
            series={seriesMonthly}
            type="bar"
            height={isMobile ? 360 : 500}
          />
        </ChartContainer>
      </ScrollableContainer>

      {isMobile && (
        <p className="text-muted mb-2" style={{ fontSize: '12px' }}>
          Ketuk bar pada grafik untuk melihat detail pada tabel
        </p>
      )}

      <DataTable
        columns={columnsMonthly}
        data={periodRows}
        expandableRows
        expandOnRowClicked
        pointerOnHover
        highlightOnHover
        expandableRowsComponent={PeriodBreakdownRow}
        renderMobileCard={(item, index) => (
          <MobilePeriodCard
            row={item}
            isSelected={selectedPoint === index}
            onClose={() => setSelectedPoint(null)}
          />
        )}
        progressPending={res?.isFetching}
        progressComponent={
          <div style={{ margin: '100px 0px' }}>
            <Spinner color="primary" />
          </div>
        }
      />
    </>
  )
}

export default MonthlyTab
